import React from 'react';
import { Form, Alert } from 'react-bootstrap';
import { FaCog, FaInfoCircle, FaPlus, FaRedo } from 'react-icons/fa';
import './ModifierConfigurationSection.scss';

interface ModifierConfigurationSectionProps {
  modifierName: string;
  setModifierName: (name: string) => void;
  priceSetter: 'add' | 'replace';
  setPriceSetter: (setter: 'add' | 'replace') => void;
  mandatory: boolean;
  setMandatory: (mandatory: boolean) => void;
  currentItemName: string;
  optionsCount: number;
}

const ModifierConfigurationSection: React.FC<ModifierConfigurationSectionProps> = ({
  modifierName,
  setModifierName,
  priceSetter,
  setPriceSetter,
  mandatory,
  setMandatory,
  currentItemName,
  optionsCount
}) => {
  const generateExplanationMessage = () => {
    const hasName = modifierName.trim();
    const itemName = currentItemName || 'este producto';
    
    if (!hasName) {
      return 'Configura el nombre del modificador para ver cómo funcionará.';
    }
    
    if (optionsCount === 0) {
      return `Agrega opciones al modificador "${modifierName}" para ver cómo funcionará.`;
    }
    
    const mandatoryText = mandatory ? 'deberá elegir' : 'podrá elegir opcionalmente';
    const priceText = priceSetter === 'add' ? 
      'se agregará al precio base del producto' : 
      'reemplazará el precio base del producto';
    
    return `Cuando tu cliente compre un ${itemName}, ${mandatoryText} una opción de "${modifierName}". El precio de la opción seleccionada ${priceText}.`;
  };

  return (
    <div className="modifier-configuration-section">
      
      <div className="configuration-form">
        {/* Modifier Name */}
        <div className="form-group">
          <Form.Label>Nombre del Modificador *</Form.Label>
          <Form.Control
            type="text"
            value={modifierName}
            onChange={(e) => setModifierName(e.target.value)}
            placeholder="Ej: Nivel de cocción, Extras, Temperatura"
            className="form-control-modern"
          />
        </div>

        {/* Price Type Selector */}
        <div className="form-group">
          <Form.Label>Tipo de Precio *</Form.Label>
          <div className="price-type-selector">
            <div 
              className={`price-option ${priceSetter === 'add' ? 'active' : ''}`}
              onClick={() => setPriceSetter('add')}
            >
              <div className="option-icon">
                <FaPlus />
              </div>
              <div className="option-content">
                <h6>Agregar al Precio</h6>
                <p>El precio de la opción se suma al precio base del producto</p>
                <div className="example">Ej: Producto $10 + Opción $3 = $13</div>
              </div>
            </div>
            
            <div 
              className={`price-option ${priceSetter === 'replace' ? 'active' : ''}`}
              onClick={() => setPriceSetter('replace')}
            >
              <div className="option-icon">
                <FaRedo />
              </div>
              <div className="option-content">
                <h6>Reemplazar Precio</h6>
                <p>El precio de la opción reemplaza completamente el precio base</p>
                <div className="example">Ej: Producto $10 → Opción $15 = $15</div>
              </div>
            </div>
          </div>
        </div>

        {/* Mandatory Selector */}
        <div className="form-group">
          <Form.Label>Tipo de Selección *</Form.Label>
          <div className="mandatory-selector">
            <div 
              className={`mandatory-option ${!mandatory ? 'active' : ''}`}
              onClick={() => setMandatory(false)}
            >
              <div className="option-content">
                <h6>Opcional</h6>
                <p>El cliente puede seleccionar una opción o dejarla en blanco</p>
              </div>
            </div>
            
            <div 
              className={`mandatory-option ${mandatory ? 'active' : ''}`}
              onClick={() => setMandatory(true)}
            >
              <div className="option-content">
                <h6>Obligatorio</h6>
                <p>El cliente debe seleccionar una opción antes de continuar</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Explanation */}
      <Alert variant="info" className="explanation-alert">
        <div className="explanation-content">
          <FaInfoCircle className="explanation-icon" />
          <div className="explanation-text">
            <h6>¿Cómo funcionará este modificador?</h6>
            <p>{generateExplanationMessage()}</p>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default ModifierConfigurationSection;
